<template>
  <g
    @mouseenter="hovered = true"
    @mouseleave="hovered = false"
  >
    <!-- Base edge path -->
    <BaseEdge
      :id="id"
      :source="source"
      :target="target"
      :style="style"
      :marker-end="markerEnd"
      :path="edgePath?.path"
    />
    <!-- Invisible wide hit-area so the thin bezier is easy to hover for the edge controls. Only
         interactive on an editable edge, so it never eats clicks/hover on read-only canvases. -->
    <path
      v-if="data?.editable"
      :d="edgePath?.path"
      fill="none"
      stroke="transparent"
      stroke-width="20"
      style="pointer-events: stroke; cursor: pointer"
    />
  </g>

  <!-- Editor-only edge controls at the midpoint: edit (open the parent-services modal for the child)
       and delete (remove this relationship immediately — reversible by redrawing, so no confirm).
       Both appear on hover only. The wrapper keeps its own hover state so moving from the line onto
       the buttons doesn't dismiss them across the SVG→overlay gap. -->
  <EdgeLabelRenderer v-if="data?.editable">
    <div
      class="nodrag nopan edge-controls"
      :class="{ 'edge-controls-visible': hovered }"
      :style="{
        position: 'absolute',
        transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
        pointerEvents: 'all',
      }"
      @mouseenter="hovered = true"
      @mouseleave="hovered = false"
    >
      <button
        type="button"
        class="edge-control-btn edge-control-edit d-flex align-items-center justify-content-center"
        title="Edit parent services"
        @click.stop="onEditClick"
      >
        <i class="bi bi-diagram-3"></i>
      </button>
      <button
        type="button"
        class="edge-control-btn edge-control-delete d-flex align-items-center justify-content-center"
        title="Remove connection"
        @click.stop="onDeleteClick"
      >
        <i class="bi bi-x-lg"></i>
      </button>
    </div>
  </EdgeLabelRenderer>

  <!-- The filter of the child service: a funnel where the connector reaches it. Always shown when the child
       has a filter. Otherwise shown on hover and on keyboard focus, and always on a device with no hover. -->
  <EdgeLabelRenderer v-if="drawsFilterIcon">
    <div
      class="nodrag nopan"
      :style="{
        position: 'absolute',
        transform: `translate(-50%, -50%) translate(${filterIconX}px, ${filterIconY}px)`,
        pointerEvents: 'all',
      }"
      @mouseenter="hovered = true"
      @mouseleave="hovered = false"
    >
      <button
        type="button"
        class="edge-control-btn edge-filter d-flex align-items-center justify-content-center"
        :class="{ 'edge-filter-visible': filter?.hasFilter || hovered, 'edge-filter-set': filter?.hasFilter }"
        :title="filter?.hasFilter ? 'Edit the filter' : 'Add a filter'"
        :aria-label="filter?.hasFilter ? 'Edit the filter' : 'Add a filter'"
        @click.stop="onFilterClick"
      >
        <i class="bi" :class="filter?.hasFilter ? 'bi-funnel-fill' : 'bi-funnel'"></i>
      </button>
    </div>
  </EdgeLabelRenderer>

  <!-- Animated icon using CSS offset-path -->
  <g v-if="data?.showFlowingIcon">
    <!-- Animated circle using CSS -->
    <circle
      :key="`${id}-${data?.animationKey || Date.now()}`"
      r="4"
      :fill="data.iconColor || '#3b82f6'"
      stroke="white"
      stroke-width="1"
      class="css-animated-icon"
      :style="{
        offsetPath: `path('${edgePath?.path}')`,
        offsetDistance: '0%',
      }"
    />
  </g>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { BaseEdge, EdgeLabelRenderer, getBezierPath, Position } from "@vue-flow/core";
import type { EdgeProps } from "@vue-flow/core";
import type { EdgeFilter } from "../utils/edgeFilter";

const props = defineProps<EdgeProps>();

const hovered = ref(false);

function onDeleteClick() {
  (props.data as { onDelete?: () => void } | undefined)?.onDelete?.();
}

function onEditClick() {
  (props.data as { onEdit?: () => void } | undefined)?.onEdit?.();
}

// What this connector shows about the child's filter. Undefined on a read-only canvas and for a child that
// is never filtered.
const filter = computed(() => (props.data as { filter?: EdgeFilter } | undefined)?.filter);

// The icon sits on the connector just before it reaches the child. Every connector into one child ends at
// the same handle, so their icons land on one spot and read as one icon.
//
//   top-to-bottom layout        left-to-right layout
//         |                         ---- (icon) -[child]
//       (icon)   17px above
//      [child]                      17px to the left
const FILTER_ICON_GAP = 17;
const entersFromLeft = computed(() => props.targetPosition === Position.Left);
const filterIconX = computed(() => (entersFromLeft.value ? props.targetX - FILTER_ICON_GAP : props.targetX));
const filterIconY = computed(() => (entersFromLeft.value ? props.targetY : props.targetY - FILTER_ICON_GAP));

// The connector from the child's first parent keeps its icon in the page at rest, so a child with several
// parents has one icon to see and one to Tab to. Any other connector draws its own only under the pointer.
const drawsFilterIcon = computed(() => {
  if (!filter.value) return false;
  return filter.value.isFirstParent || hovered.value;
});

function onFilterClick(event: MouseEvent) {
  const icon = event.currentTarget as HTMLElement;
  const openPopover = (props.data as { onFilter?: (anchor: DOMRect) => void } | undefined)?.onFilter;
  openPopover?.(icon.getBoundingClientRect());
}

const edgePath = computed(() => {
  const pathData = getBezierPath({
    sourceX: props.sourceX,
    sourceY: props.sourceY,
    sourcePosition: props.sourcePosition,
    targetX: props.targetX,
    targetY: props.targetY,
    targetPosition: props.targetPosition,
  });
  return {
    path: pathData[0], // Path string is the first element
    labelX: pathData[1], // Label X is second element
    labelY: pathData[2], // Label Y is third element
  };
});

// Default label position (center of edge)
const labelX = computed(() => edgePath.value?.labelX || 0);
const labelY = computed(() => edgePath.value?.labelY || 0);
</script>

<style scoped lang="scss">
/* Container of the edge's hover controls. Hidden until the line (or the container itself) is
   hovered. The first reveal comes from hovering the SVG line — which stays interactive — so the
   container becoming visible then sustains its own hover. A small pad gives a forgiving hit zone. */
.edge-controls {
  display: flex;
  gap: 4px;
  padding: 6px;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.12s ease;
}

.edge-controls-visible {
  opacity: 1;
  visibility: visible;
}

/* Match the service-node toolbar buttons (rounded square, same tokens) so the edge controls read as
   the same family — see .toolbar-btn in ServiceNode.vue. */
.edge-control-btn {
  width: 24px;
  height: 24px;
  padding: 0;
  border: 1px solid var(--bs-border-color);
  border-radius: var(--bs-border-radius-sm);
  background: var(--bs-body-bg);
  color: var(--bs-secondary-color);
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  transition: var(--bs-transition);
  box-shadow: var(--bs-box-shadow-sm);

  &:hover {
    transform: scale(1.1);
    box-shadow: var(--bs-box-shadow);
  }

  &:active {
    transform: scale(0.95);
  }
}

.edge-control-edit:hover {
  color: var(--bs-primary);
}

.edge-control-delete:hover {
  color: var(--bs-danger);
}

/* The filter icon is in the page while hidden, so the keyboard can reach it, and focus shows it. */
.edge-filter {
  opacity: 0;
}

.edge-filter-visible,
.edge-filter:focus-visible {
  opacity: 1;
}

/* A device with no hover cannot reveal the icon, so it is always shown there. */
@media (hover: none) {
  .edge-filter {
    opacity: 1;
  }
}

/* The stronger look of a filter that is set. */
.edge-filter-set {
  color: var(--bs-link-color);
  border-color: var(--bs-primary-border-subtle);
}

.css-animated-icon {
  animation: moveAlongPath 1.5s linear forwards;
}

@keyframes moveAlongPath {
  0% {
    offset-distance: 0%;
    opacity: 1;
  }
  90% {
    offset-distance: 100%;
    opacity: 1;
  }
  100% {
    offset-distance: 100%;
    opacity: 0;
  }
}
</style>
